import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useRulesQuery } from 'queries/wage/useRulesQuery'
import { useRulesSearchQueryState } from '../hooks/useRulesSearchQueryState'
import { useRulesPaginationQueryState } from '../hooks/useRulesPaginationQueryState'

type Rule = NonNullable<ReturnType<typeof useRulesQuery>['data']>[number]

type RulesContextState = {
  state: {
    searchInput: string
    filteredRules: Rule[]
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setSearchInput: (input: string) => void
    setPagination: Dispatch<
      SetStateAction<RulesContextState['state']['pagination']>
    >
  }
}

const [Context, useRulesContextValue] = createContext<RulesContextState>({
  name: 'RulesContext',
})

export const RulesProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchInput, setSearchInput] = useRulesSearchQueryState()
  const [pagination, setPagination] = useRulesPaginationQueryState()
  const {
    data,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useRulesQuery()

  const filteredRules = useMemo(() => {
    if (!data) return []
    const q = searchInput.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (r) =>
        r.id.toString().includes(q) ||
        (r.reference && r.reference.toLowerCase().includes(q)) ||
        (r.jurisdictions ?? []).some((j) => j.name.toLowerCase().includes(q))
    )
  }, [data, searchInput])

  if (isLoading) {
    return <Loading />
  }

  if (isError) {
    return (
      <Message variant="error">
        Sorry, something went wrong. Please reach out to eng support if this
        persists.
      </Message>
    )
  }

  const value: RulesContextState = {
    state: {
      searchInput,
      filteredRules,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
    },
    actions: {
      refetch,
      setSearchInput,
      setPagination,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useRulesContextValue }
